"use client"

import type React from "react"
import { useState, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { useAuth } from "../../../context/AuthContext"
import { useToast } from "../../../context/ToastContext"
import { useOAuthCallback } from "../../../hooks/useOAuthCallback"
import { useConnectionStatus } from "../../../hooks/useConnectionStatus"
import { FIELD_MAPPING_CONSTANTS } from "../../../constants/field-mapping"

// Components
import LoadingScreen from "../shared/LoadingScreen"
import ErrorScreen from "../shared/ErrorScreen"
import StepIndicator from "../shared/StepIndicator"
import PlatformCard from "./PlatformCard"

import "./ConnectAccounts.css"

const ConnectAccountsConnectScreen: React.FC = () => {
  const [loading, setLoading] = useState(false)
  const [currentAction, setCurrentAction] = useState<string | null>(null)
  const navigate = useNavigate()
  const { authoriseHubspot, authoriseSalesforce } = useAuth()
  const { isProcessing, errorOAuth, successOAuth } = useOAuthCallback()
  const { error, success } = useToast()

  const { connectionStatus, updateConnectionError, disconnect } = useConnectionStatus(successOAuth)

  const handleHubspotConnect = useCallback(async () => {
    try {
      setLoading(true)
      setCurrentAction("Connecting to HubSpot...")

      const response = await authoriseHubspot()

      if (response.status === 200) {
        const hubspotAuthUrl = `${response.data}`
        window.location.href = hubspotAuthUrl
      } else {
        throw new Error("Failed to get HubSpot authorization URL")
      }
    } catch (err: any) {
      const errorMessage = err.userMessage || err.message || "Failed to connect to HubSpot"
      updateConnectionError("hubspot", errorMessage)
      error(errorMessage)
    } finally {
      setLoading(false)
      setCurrentAction(null)
    }
  }, [authoriseHubspot, updateConnectionError, error])

  const handleSalesforceConnect = useCallback(async () => {
    if (!connectionStatus.hubspot.connected) {
      error(FIELD_MAPPING_CONSTANTS.ERROR_MESSAGES.HUBSPOT_FIRST)
      return
    }

    try {
      setLoading(true)
      setCurrentAction("Connecting to Salesforce...")

      const tenantId = localStorage.getItem(FIELD_MAPPING_CONSTANTS.STORAGE_KEYS.HUBSPOT_TENANT_ID)

      if (!tenantId) {
        throw new Error(FIELD_MAPPING_CONSTANTS.ERROR_MESSAGES.NO_HUBSPOT_TENANT)
      }

      const response = await authoriseSalesforce(Number.parseInt(tenantId))

      if (response.status === 200) {
        const salesforceAuthUrl = `${response.data}`
        window.location.href = salesforceAuthUrl
      } else {
        throw new Error("Failed to get Salesforce authorization URL")
      }
    } catch (err: any) {
      const errorMessage = err.userMessage || err.message || "Failed to connect to Salesforce"
      updateConnectionError("salesforce", errorMessage)
      error(errorMessage)
    } finally {
      setLoading(false)
      setCurrentAction(null)
    }
  }, [connectionStatus.hubspot.connected, authoriseSalesforce, updateConnectionError, error])

  const handleDisconnect = useCallback(
    (provider: "hubspot" | "salesforce") => {
      disconnect(provider)
      success(`${provider === "hubspot" ? "HubSpot" : "Salesforce"} disconnected successfully`)
    },
    [disconnect, success],
  )

  const handleBack = useCallback(() => {
    navigate(FIELD_MAPPING_CONSTANTS.ROUTES.CONNECTORS)
  }, [navigate])

  const handleNext = useCallback(() => {
    if (connectionStatus.hubspot.connected && connectionStatus.salesforce.connected) {
      localStorage.setItem(FIELD_MAPPING_CONSTANTS.STORAGE_KEYS.CONNECTOR_FLOW_STEP, "2")
      navigate("/dashboard/choose-sync")
    }
  }, [connectionStatus, navigate])

  // Show processing screen during OAuth
  if (isProcessing || loading) {
    return (
      <LoadingScreen
        title={currentAction || "Processing Authentication..."}
        description="Please wait while we connect your account."
      />
    )
  }

  // Show error screen if OAuth failed
  if (errorOAuth) {
    return <ErrorScreen title="Authentication Error" message={errorOAuth} onRetry={() => window.location.reload()} />
  }

  const canProceed = connectionStatus.hubspot.connected && connectionStatus.salesforce.connected

  return (
    <div className="connect-accounts">
      <div className="connect-accounts-header">
        <h1>Connect Your Accounts</h1>
        <p>Authorize access to your Salesforce and HubSpot accounts to keep your data in sync</p>
        <StepIndicator currentStep={2} stepLabel="Connect Accounts" completedSteps={[1]} />
      </div>

      <div className="platforms-section">
        <PlatformCard
          platform="hubspot"
          connection={connectionStatus.hubspot}
          onConnect={handleHubspotConnect}
          onDisconnect={() => handleDisconnect("hubspot")}
          loading={loading}
        />

        <div className="sync-icon">
          {/* <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2">
            <path d="M23 4v6h-6" />
            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
            <path d="M1 20v-6h6" />
            <path d="M3.51 9a9 9 0 0 1 2.12 9.36L1 14" />
          </svg> */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 112.62 120.72"
            width="500"
            height="500"
            fill="#3b82f6"
          >
            <title>circle arrow</title>
            <path d="M11.64,100.12l-.4-.47-1.06,8.63a5.08,5.08,0,0,1-1.92,3.41A5.11,5.11,0,0,1,0,107L2.79,84.65v-.07a3.28,3.28,0,0,1,.08-.41h0A5.09,5.09,0,0,1,9,80.39q11.22,2.53,22.42,5.15a5,5,0,0,1,3.17,2.25,5.14,5.14,0,0,1,.64,3.84v0a5,5,0,0,1-2.25,3.16,5.08,5.08,0,0,1-3.83.65c-3.31-.75-6.62-1.52-9.92-2.28a40.71,40.71,0,0,0,2.84,3,50.09,50.09,0,0,0,26.23,13.49,48.67,48.67,0,0,0,14.71.34A47.35,47.35,0,0,0,77,106h0q2.52-1.19,4.83-2.54c1.56-.93,3.07-1.92,4.51-3a50.8,50.8,0,0,0,8.56-7.88,48.92,48.92,0,0,0,6.39-9.45l.56-1.1,10,2.69-.8,1.66a58.64,58.64,0,0,1-7.9,12.24,61.28,61.28,0,0,1-10.81,10.1c-1.68,1.23-3.46,2.4-5.32,3.5s-3.73,2.07-5.74,3a58,58,0,0,1-17,5,58.56,58.56,0,0,1-17.79-.39,60.21,60.21,0,0,1-31.58-16.26c-1.2-1.16-2.26-2.31-3.24-3.45ZM101,20.6l.4.47,1-8.63a5.11,5.11,0,1,1,10.14,1.26l-2.74,22.37,0,.07c0,.13,0,.27-.07.41h0a5.09,5.09,0,0,1-6.08,3.78c-7.47-1.69-15-3.4-22.42-5.15a5,5,0,0,1-3.16-2.25,5.1,5.1,0,0,1-.65-3.84v0a5,5,0,0,1,2.25-3.16,5.1,5.1,0,0,1,3.84-.65c3.31.75,6.61,1.52,9.92,2.28-.84-1-1.77-2-2.84-3.05a50.09,50.09,0,0,0-12.13-8.73A49.49,49.49,0,0,0,64.37,11a48.6,48.6,0,0,0-14.7-.34,47.26,47.26,0,0,0-14,4.1h0q-2.53,1.18-4.83,2.54c-1.57.93-3.07,1.92-4.52,3a50.34,50.34,0,0,0-8.55,7.88,48,48,0,0,0-6.39,9.45l-.57,1.1L.76,36l.8-1.66A58.9,58.9,0,0,1,9.46,22.1,61.63,61.63,0,0,1,20.27,12q2.54-1.85,5.32-3.5c1.81-1.06,3.73-2.07,5.74-3a58,58,0,0,1,17-5A58.56,58.56,0,0,1,66.16.89a59.77,59.77,0,0,1,17,5.74A60.4,60.4,0,0,1,97.75,17.15c1.19,1.16,2.26,2.31,3.24,3.45Z" />
          </svg>
        </div>

        <PlatformCard
          platform="salesforce"
          connection={connectionStatus.salesforce}
          onConnect={handleSalesforceConnect}
          onDisconnect={() => handleDisconnect("salesforce")}
          loading={loading}
          disabled={!connectionStatus.hubspot.connected}
        />
      </div>

      <div className="note-section">
        <div className="note">
          <span className="note-icon">💡</span>
          <span className="note-text">Connect HubSpot first, then Salesforce to continue.</span>
        </div>
        <div className="note">
          <span className="note-icon">💡</span>
          <span className="note-text">Enable the "Change Data Capture" setting in connected salesforce org .</span>
        </div>
      </div>

      <div className="features-section">
        <h2>What You Can Do With NetzSync</h2>
        <div className="features-list">
          <div className="feature-item">
            <div className="CA-feature-icon">🔄</div>
            <span>Sync contacts, tasks, and tickets between Salesforce and HubSpot</span>
          </div>
          <div className="feature-item">
            <div className="CA-feature-icon">⚙️</div>
            <span>Configure uni or bi-directional sync</span>
          </div>
          <div className="feature-item">
            <div className="CA-feature-icon">📊</div>
            <span>Track sync status and view logs</span>
          </div>
        </div>
      </div>

      <div className="actions-section">
        <button className="back-button" onClick={handleBack}>
          Back
        </button>
        <button className="next-button" onClick={handleNext} disabled={!canProceed}>
          Next
        </button>
      </div>
    </div>
  )
}

export default ConnectAccountsConnectScreen
